import _ from "lodash";

import AppConstants from "../constants/AppConstants";
import IDUtils from "./IDUtils";

/**
 * This is utility class used to provide some common functions that are need by blocks and elements within a design
 *
 */
class BlockUtils {
  constructor() {}

  /**
   * Determines if the item is visible in the specified layout
   * @param item - Immutable object with visible property
   * @param layout
   * @returns {*|boolean}
   */
  isVisibleInLayout(item, layout) {
    var vis = item.get("visible");
    return layout.startsWith(vis) || vis == AppConstants.VISIBLE_ALL;
  }

  isSiteBlock(block) {
    return AppConstants.SITE_ITEM_SITE === block.get("pType");
  }

  isUsingSiteBlock(block) {
    return block.has(AppConstants.BLOCK_PROP_PARENT_BLOCK_ID);
  }

  hasSiteBlock(block) {
    return block.has(AppConstants.BLOCK_PROP_PARENT_BLOCK);
  }

  isBlockSelectedInLayout(block, layout) {
    return !block.isEmpty() && block.getIn(["selected", "layout"]) == layout;
  }

  isElementSelectedInLayout(element, layout) {
    var selected = element.get("selected");
    return _.isObject(selected) && selected.get("layout") == layout;
  }

  getElementLayoutSelectionState(element, layout) {
    let selected = element.get("selected");

    if (_.isObject(selected)) {
      return {
        selected: selected.get("layout") == layout,
        edit: selected.get("edit", false),
      };
    } else {
      return { selected: false, edit: false };
    }
  }

  getElementID(baseId, elemIndex) {
    return IDUtils.getID(baseId, "elements", elemIndex);
  }

  getBlockElement(block, elementId) {
    var idPath = IDUtils.getArray(elementId);
    if (idPath[0] == block.get("id")) {
      idPath.shift();
    }
    return block.getIn(idPath);
  }

  getChildParent(block, element) {
    const baseSelector = element?.selected?.id.split(".");
    const parentSelector = baseSelector.slice(1, baseSelector.length - 2);
    return block.getIn(parentSelector);
  }

  getBlockTopPadding(block, layout) {
    var pad = 0;

    var elements = block.get("elements");
    var states = block.get("states");

    var minY = this._getElementsMinY(elements, layout);
    pad = Math.min(pad, minY);

    if (states) {
      states.forEach(function (state) {
        elements = state.get("elements");

        minY = this._getElementsMinY(elements, layout);
        pad = Math.min(pad, minY);
      }, this);
    }

    pad = pad < 0 ? Math.abs(pad) : 0;

    return pad;
  }

  _getElementsMinY(elements, layout) {
    var val = 0;

    elements.forEach(function (elem) {
      val = Math.min(val, elem.getIn([layout, "y"], 0));
    });

    return val;
  }

  getBlockBottomPadding(block, layout) {
    var blockHeight = block.getIn([layout, "h"], 0);
    var pad = blockHeight;

    var elements = block.get("elements");
    var states = block.get("states");

    var maxY = this._getElementsMaxY(elements, layout);

    pad = Math.max(pad, maxY);

    if (states) {
      states.forEach(function (state) {
        elements = state.get("elements");

        maxY = this._getElementsMaxY(elements, layout);
        pad = Math.max(pad, maxY);
      }, this);
    }

    pad = pad > blockHeight ? pad - blockHeight : 0;

    return pad;
  }

  _getElementsMaxY(elements, layout) {
    var val = 0;

    elements.forEach(function (elem) {
      val = Math.max(val, elem.getIn([layout, "y"], 0) + elem.getIn([layout, "h"], 0));
    });

    return val;
  }

  /**
   * Takes an id that was created by getElementID or getStateID and removes the blockId it and returns a path
   * @param elementId
   * @returns {array}
   */
  getElementPath(elementId) {
    return IDUtils.getArray(elementId, 1);
  }

  getStateID(baseId, stateIndex) {
    return IDUtils.getID(baseId, "states", stateIndex);
  }

  getSiteBlockStateID(blockId, stateIndex) {
    return IDUtils.getID(blockId, AppConstants.BLOCK_PROP_PARENT_BLOCK, "states", stateIndex);
  }

  getStateElements(block, stateId) {
    return block.getIn(IDUtils.getArray(stateId, 1).concat("elements"));
  }

  /**
   * Takes a string path in the form Somepath.another.another.1 and removes the index value at the returning the new path
   * @param path
   */
  trimIndexFromPath(path) {
    return path.replace(/\.\d+$/gi, "");
  }
}

export default new BlockUtils();
